import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { environmentURLs } from '../../../components/common/CaptureEnvironment';
import { client } from '../../../utils/apolloClient';
import {
  API_DIRECTORY_PAGE,
  EMAIL_MESSAGE,
  API_TYPES,
} from '../../../constants/APIDirectoryConstants';
import { submitRestrictedAccessRequest } from '../orderAPI/orderAPI.slice';

export const normalizePlanNames = (items) => {
  if (!Array.isArray(items)) return items;
  
  return items.map((item) => ({
    ...item,
    plans: Array.isArray(item.plans)
      ? item.plans.map((plan) => ({
          ...plan,
          name: plan.name ?? plan.planName,
        }))
      : item.plans,
  }));
};

export const initialState = {
  componentToRender: JSON.parse(localStorage.getItem('componentToRender')) || {
    page: API_DIRECTORY_PAGE.HOME,
    props: {},
  },
  apiInfo: JSON.parse(localStorage.getItem('apiResponse')) || null,
  fetchingAPIDetails: false,
  errorInFetchingAPIDetails: false,
  fetchingAPIDetailsInBundle: false,
  errorInFetchingAPIDetailsInBundle: false,
  apiInfoInBundle: null,
  apiSpecInBundle: null,
  fetchingAPISpecInBundle: false,
  errorInFetchingAPISpecInBundle: false,
  consumerEmailMessage: null,
  providerEmailMessage: null,
  domainsLoading: false,
  domains: {},
  errorFetchingDomains: false,
  apis: [],
  bundles: [],
  bundleDetails: {},
  bundleDetailsViewEnabled: false,
  updatedBundles: [],
  bundleData: {},
  fetchingBundleDetails: false,
  errorInFetchingBundleDetails: false,
  errorFetchingRestApis: false,
  restApisLoading: false,
  asyncApis: [],
  errorFetchingAsyncApis: false,
  asyncApisLoading: false,
  daabConsentDetails: {},
  fetchingDAABConsentDetails: false,
  errorInFetchingDAABConsentDetails: false,
  submittingTerms: false,
  errorInAcceptTerms: false,
  prevScreen: '',
};

export const getAPIDetailsById = createAsyncThunk(
  'getAPIDetailsById',
  async ({ apiId, apiType, token }) => {
    let url = `${environmentURLs.REACT_APP_API_GATEWAY}/v3/directory/apis/${apiId}?isDealer=true`;
    if (apiType === API_TYPES.ASYNC)
      url = `${environmentURLs.REACT_APP_ASYNC_API_SERVICE}/v2/directory/async-apis/${apiId}?isDealer=true`;
    let response = await client.authorizeRequest({
      method: 'GET',
      url,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { data: response.data };
  }
);
export const getAPIDetailsByIdInBundle = createAsyncThunk(
  'getAPIDetailsByIdInBundle',
  async ({ apiId, apiType, bundleId, token }) => {
    let url = `${environmentURLs.REACT_APP_API_GATEWAY}/v3/directory/apis/${apiId}?isDealer=true`;
    if (apiType === API_TYPES.ASYNC)
      url = `${environmentURLs.REACT_APP_ASYNC_API_SERVICE}/v2/directory/async-apis/${apiId}?isDealer=true`;
    let response = await client.authorizeRequest({
      method: 'GET',
      url,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { data: response.data, bundleId };
  }
);

export const getAPISpecByIdInBundle = createAsyncThunk(
  'getAPISpecByIdInBundle',
  async ({ apiId, apiType, bundleId, token }) => {
    const apiTypeQueryParam = (apiType && apiType.toLowerCase() === API_TYPES.ASYNC)
      ? API_TYPES.ASYNC
      : API_TYPES.REST;
    const url = `${environmentURLs.REACT_APP_API_GATEWAY}/v3/directory/bundles/${bundleId}?apiId=${apiId}&apiType=${apiTypeQueryParam}`;
    let response = await client.authorizeRequest({
      method: 'GET',
      url,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { data: response.data, bundleId: bundleId };
  }
);

export const getBundleDetailsById = createAsyncThunk(
  'getBundleDetailsById',
  async ({ bundleId, token }) => {
    const url = `${environmentURLs.REACT_APP_API_GATEWAY}/v3/directory/bundles/${bundleId}`;
    let response = await client.authorizeRequest({
      method: 'GET',
      url,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { data: response.data, bundleId: bundleId };
  },
);

export const getConsentTerms = createAsyncThunk(
  'getConsentTerms',
  async ({ token }) => {
    const url = `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/consent?resolveConsentDocument=true`;
    let response = await client.authorizeRequest({
      method: 'GET',
      url,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { data: response.data };
  }
);

export const acceptTerms = createAsyncThunk(
  'acceptTerms',
  async ({ payload, token }) => {
    let response = await client.authorizeRequest({
      method: 'POST',
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/consent`,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      data: { ...payload },
    });
    return { data: response.data };
  }
);

export const getDomains = createAsyncThunk('getDomains', async ({ token }) => {
  const response = await client.authorizeRequest({
    method: 'GET',
    url: `${environmentURLs.REACT_APP_API_GATEWAY}/v3/directory/groups`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return { data: response.data };
});

export const getApis = createAsyncThunk(
  'getApis',
  async ({ token, page = 1, pageSize = 100,isDealerPurchaseDisabled }) => {
    const baseUrl = `${environmentURLs.REACT_APP_API_GATEWAY}/v3/directory?view=summary&pageSize=${pageSize}&isDealer=true`;
    const contractedOnlyParam =
      typeof isDealerPurchaseDisabled === 'boolean'
        ? `&contractedOnly=${isDealerPurchaseDisabled}`
        : '';

    const fetchPage = async (pageNumber) => {
      return client.authorizeRequest({
        method: 'GET',
        url: `${baseUrl}&page=${pageNumber}${contractedOnlyParam}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        json: true,
      });
    };

    const firstResponse = await fetchPage(page);
    const firstData = firstResponse?.data || {};

    const totalPages = Number.isFinite(firstData.totalPages)
      ? firstData.totalPages
      : 1;

    let allBundles = Array.isArray(firstData.bundles) ? firstData.bundles : [];
    const allApis = Array.isArray(firstData.items) ? [...firstData.items] : [];

    const remainingPages = Array.from(
      { length: Math.max(0, totalPages - page) },
      (_, idx) => page + idx + 1
    );

    if (remainingPages.length) {
      const remainingResults = await Promise.all(
        remainingPages.map(async (pageNumber) => {
          const response = await fetchPage(pageNumber);
          return { pageNumber, data: response?.data || {} };
        })
      );

      remainingResults
        .sort((a, b) => a.pageNumber - b.pageNumber)
        .forEach(({ data }) => {
          if (!allBundles.length && Array.isArray(data.bundles)) {
            allBundles = data.bundles;
          }
          if (Array.isArray(data.items) && data.items.length) {
            allApis.push(...data.items);
          }
        });
    }

    return { allApis, allBundles };
  }
);
export const calculateUpdatedBundles = (apis, asyncApis, bundles) => {
  const allApis = [...asyncApis, ...apis];
  const apiDataById = allApis.reduce((acc, api) => {
    acc[api.id] = {
      visibility: api.visibility,
      description: api.description,
      plans: api.plans,
      isContracted: api.isContracted,
    };
    return acc;
  }, {});

  return bundles.map((bundle) => {
    const updatedApis = bundle.apis.map((api) => {
      const apiDetails = apiDataById[api.id] || {};
      return {
        ...api,
        visibility: apiDetails?.visibility || '',
        description: apiDetails?.description || 'No description available',
        plans: apiDetails?.plans,
        isContracted: apiDetails?.isContracted,
      };
    });

    return {
      ...bundle,
      apis: updatedApis,
    };
  });
};
export const updateBundleWithApiDetails = (apiInfo, bundleId, bundleData) => {
  // If bundleData doesn't match the current bundleId, return it unchanged
  if (!bundleData || bundleData.id !== bundleId) {
    return bundleData;
  }

  // Create a deep copy of the bundleData to avoid mutating the original
  const updatedBundle = JSON.parse(JSON.stringify(bundleData));

  // Find the index of the API in the bundle's apis array that matches the apiInfo
  const apiIndex = updatedBundle.apis.findIndex(api => api.id === apiInfo.id);

  if (apiIndex !== -1) {
    // Update the API details in the bundle
    updatedBundle.apis[apiIndex] = {
      ...updatedBundle.apis[apiIndex],
      ...apiInfo
    };
  }
  return updatedBundle;
};

export const getAsyncApis = createAsyncThunk(
  'getAsyncApis',
  async ({ token, page = 1, pageSize = 100, isDealerPurchaseDisabled}) => {
    const baseUrl = `${environmentURLs.REACT_APP_ASYNC_API_SERVICE}/v2/directory/async-apis?view=summary&pageSize=${pageSize}&isDealer=true`;
    const contractedOnlyParam =
      typeof isDealerPurchaseDisabled === 'boolean'
        ? `&contractedOnly=${isDealerPurchaseDisabled}`
        : '';

    const fetchPage = async (pageNumber) => {
      return client.authorizeRequest({
        method: 'GET',
        url: `${baseUrl}&page=${pageNumber}${contractedOnlyParam}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        json: true,
      });
    };

    const firstResponse = await fetchPage(page);
    const firstData = firstResponse?.data || {};

    const totalPages = Number.isFinite(firstData.totalPages)
      ? firstData.totalPages
      : 1;

    const allAsyncApis = Array.isArray(firstData.items) ? [...firstData.items] : [];

    const remainingPages = Array.from(
      { length: Math.max(0, totalPages - page) },
      (_, idx) => page + idx + 1
    );

    if (remainingPages.length) {
      const remainingResults = await Promise.all(
        remainingPages.map(async (pageNumber) => {
          const response = await fetchPage(pageNumber);
          return { pageNumber, data: response?.data || {} };
        })
      );

      remainingResults
        .sort((a, b) => a.pageNumber - b.pageNumber)
        .forEach(({ data }) => {
          if (Array.isArray(data.items) && data.items.length) {
            allAsyncApis.push(...data.items);
          }
        });
    }

    return allAsyncApis;
  }
);

export const sendEmailToConsumer = createAsyncThunk(
  'sendEmailToConsumer',
  async ({ payload, token }) => {
    const response = await client.authorizeRequest({
      method: 'POST',
      url: `${environmentURLs.REACT_APP_API_EMAIL_DISPATCHER_SERVICE}`,
      data: payload,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { data: response.data };
  }
);

export const sendEmailToProvider = createAsyncThunk(
  'sendEmailToProvider',
  async ({ payload, token }) => {
    const response = await client.authorizeRequest({
      method: 'POST',
      url: `${environmentURLs.REACT_APP_API_EMAIL_DISPATCHER_SERVICE}`,
      data: payload,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { data: response.data };
  }
);

export const apiDirectorySlice = createSlice({
  name: 'apiDirectorySlice',
  initialState,
  reducers: {
    setAPIDirectoryPage: (state, action) => {
      state.componentToRender = action.payload;
      state.bundleDetailsViewEnabled =
        action.payload?.props?.bundleDetailsViewEnabled;
      state.prevScreen = action.payload?.props?.prevScreen;
      try {
        localStorage.setItem('componentToRender', JSON.stringify(action.payload));
       } catch (e) {
         // Ignore storage failures (quota exceeded, storage disabled, etc.)
       }
    },
  },
  extraReducers: {
    [getAPIDetailsById.pending]: (state) => {
      state.apiInfo = null;
      state.fetchingAPIDetails = true;
      state.errorInFetchingAPIDetails = false;
    },
    [getAPIDetailsById.fulfilled]: (state, action) => {
      state.apiInfo = action.payload;
      state.fetchingAPIDetails = false;
      state.errorInFetchingAPIDetails = false;
      try {
        localStorage.setItem('apiResponse', JSON.stringify(action.payload));
       } catch (e) {
         // Ignore storage failures (quota exceeded, storage disabled, etc.)
       }
    },
    [getAPIDetailsById.rejected]: (state) => {
      state.apiInfo = null;
      state.fetchingAPIDetails = false;
      state.errorInFetchingAPIDetails = true;
    },
    [getAPIDetailsByIdInBundle.pending]:(state)=>{
      state.apiInfoInBundle = null;
      state.fetchingAPIDetailsInBundle = true;
      state.errorInFetchingAPIDetailsInBundle = false;
    },
    [getAPIDetailsByIdInBundle.fulfilled]:(state,action)=>{
      state.apiInfoInBundle = action.payload.data;
      state.fetchingAPIDetailsInBundle = false;
      state.errorInFetchingAPIDetailsInBundle = false;
       const updatedBundle = updateBundleWithApiDetails(
        action.payload.data,
        action.payload.bundleId,
        state.bundleData
      );
      state.bundleData = updatedBundle;
    },
    [getAPIDetailsByIdInBundle.rejected]:(state)=>{
      state.apiInfoInBundle = null;
      state.fetchingAPIDetailsInBundle = false;
      state.errorInFetchingAPIDetailsInBundle = true;
    },
    [getAPISpecByIdInBundle.pending]:(state)=>{
      state.apiSpecInBundle = null;
      state.fetchingAPISpecInBundle = true;
      state.errorInFetchingAPISpecInBundle = false;
    },
    [getAPISpecByIdInBundle.fulfilled]:(state,action)=>{
      state.apiSpecInBundle = action.payload.data;
      state.fetchingAPISpecInBundle = false;
      state.errorInFetchingAPISpecInBundle = false;
      const updatedBundle = updateBundleWithApiDetails(
        action.payload.data,
        action.payload.bundleId,
        state.bundleData
      );
      state.bundleData = updatedBundle;
    },
    [getAPISpecByIdInBundle.rejected]:(state)=>{
      state.apiSpecInBundle = null;
      state.fetchingAPISpecInBundle = false;
      state.errorInFetchingAPISpecInBundle = true;
    },
    [getBundleDetailsById.pending]: (state) => {
      state.bundleData = null;
      state.fetchingBundleDetails = true;
      state.errorInFetchingBundleDetails = false;
    },
    [getBundleDetailsById.fulfilled]: (state, action) => {
      state.bundleData = action.payload.data;
      state.fetchingBundleDetails = false;
      state.errorInFetchingBundleDetails = false;
    },
    [getBundleDetailsById.rejected]: (state) => {
      state.bundleData = null;
      state.fetchingBundleDetails = false;
      state.errorInFetchingBundleDetails = true;
    },
    [getConsentTerms.pending]: (state) => {
      state.daabConsentDetails = null;
      state.fetchingDAABConsentDetails = true;
      state.errorInFetchingDAABConsentDetails = false;
    },
    [getConsentTerms.fulfilled]: (state, action) => {
      state.daabConsentDetails = action.payload.data;
      state.fetchingDAABConsentDetails = false;
      state.errorInFetchingDAABConsentDetails = false;
    },
    [getConsentTerms.rejected]: (state) => {
      state.daabConsentDetails = null;
      state.fetchingDAABConsentDetails = false;
      state.errorInFetchingDAABConsentDetails = true;
    },
    [acceptTerms.pending]: (state) => {
      state.daabConsentDetails = null;
      state.submittingTerms = true;
      state.errorInAcceptTerms = false;
    },
    [acceptTerms.fulfilled]: (state, action) => {
      state.daabConsentDetails = action.payload.data.result;
      state.submittingTerms = false;
      state.errorInAcceptTerms = false;
    },
    [acceptTerms.rejected]: (state) => {
      state.daabConsentDetails = null;
      state.submittingTerms = false;
      state.errorInAcceptTerms = true;
    },
    [sendEmailToConsumer.pending]: (state) => {
      state.consumerEmailMessage = EMAIL_MESSAGE.SENDING;
    },
    [sendEmailToConsumer.fulfilled]: (state) => {
      state.consumerEmailMessage = EMAIL_MESSAGE.SUCCESS;
    },
    [sendEmailToConsumer.rejected]: (state) => {
      state.consumerEmailMessage = EMAIL_MESSAGE.FAILURE;
    },
    [sendEmailToProvider.pending]: (state) => {
      state.providerEmailMessage = EMAIL_MESSAGE.SENDING;
    },
    [sendEmailToProvider.fulfilled]: (state) => {
      state.providerEmailMessage = EMAIL_MESSAGE.SUCCESS;
    },
    [sendEmailToProvider.rejected]: (state) => {
      state.providerEmailMessage = EMAIL_MESSAGE.FAILURE;
    },
    [getDomains.pending]: (state) => {
      state.domainsLoading = true;
      state.errorFetchingDomains = false;
    },
    [getDomains.fulfilled]: (state, { payload }) => {
      state.domainsLoading = false;
      state.errorFetchingDomains = false;
      state.domains = payload.data;
    },
    [getDomains.rejected]: (state) => {
      state.domainsLoading = false;
      state.errorFetchingDomains = true;
    },
    [getApis.pending]: (state) => {
      state.restApisLoading = true;
      state.errorFetchingRestApis = false;
    },
    [getApis.fulfilled]: (state, { payload }) => {
      state.restApisLoading = false;
      state.errorFetchingRestApis = false;
      state.apis = normalizePlanNames(payload.allApis);
      state.bundles = normalizePlanNames(payload.allBundles);
      state.apisObject = state.apis.reduce((prev, { id, ...rest }) => {
        prev[id] = rest;
        return prev;
      }, {});
      state.bundlesObject = state.bundles.reduce((prev, { id, ...rest }) => {
        prev[id] = {kind: 'bundle', ...rest};
        return prev;
      }, {});
      state.updatedBundles = calculateUpdatedBundles(
        state.apis,
        state.asyncApis,
        state.bundles
      );
    },
    [getApis.rejected]: (state) => {
      state.restApisLoading = false;
      state.errorFetchingRestApis = true;
    },
    [getAsyncApis.pending]: (state) => {
      state.asyncApisLoading = true;
      state.errorFetchingAsyncApis = false;
    },
    [getAsyncApis.fulfilled]: (state, { payload }) => {
      state.asyncApisLoading = false;
      state.errorFetchingAsyncApis = false;
      state.asyncApis = normalizePlanNames(payload);
      state.asyncApisObject = state.asyncApis.reduce((prev, { id, ...rest }) => {
        prev[id] = rest;
        return prev;
      }, {});
      state.updatedBundles = calculateUpdatedBundles(
        state.apis,
        state.asyncApis,
        state.bundles
      );
    },
    [getAsyncApis.rejected]: (state) => {
      state.asyncApisLoading = false;
      state.errorFetchingAsyncApis = true;
    },

    [submitRestrictedAccessRequest.fulfilled]: (state, action) => {
      const { apiId } = action.meta.arg;

      state.apis = state.apis.map((api) =>
        api.id === apiId ? { ...api, restrictedSharedStatus: 'requested' } : api
      );
      state.asyncApis = state.asyncApis.map((api) =>
        api.id === apiId ? { ...api, restrictedSharedStatus: 'requested' } : api
      );
      if (state.apiInfo?.data?.id === apiId) {
        state.apiInfo = {
          ...state.apiInfo,
          data: {
            ...state.apiInfo.data,
            restrictedSharedStatus: 'requested',
          },
        };
      }
    },
  },
});

export const { setAPIDirectoryPage } = apiDirectorySlice.actions;

export default apiDirectorySlice.reducer;
